import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useIsMobile } from "../hooks/useIsMobile";
import { usePageTitle } from "../hooks/usePageTitle";
import { useQuery, gql } from "@apollo/client";
import SearchBar from "../components/SearchBar";
import FilterBar from "../components/FilterBar";
import SaunaCard from "../components/SaunaCard";
import ResultsMap from "../components/ResultsMap";

const SEARCH_SAUNAS = gql`
  query SearchSaunas(
    $lat: BigFloat
    $lng: BigFloat
    $minCapacity: Int
    $maxCapacity: Int
    $maxDistanceMiles: BigFloat
    $minPriceCents: Int
    $maxPriceCents: Int
    $saunaTypes: [SaunaType!]
    $amenityIds: [Int!]
    $filterDate: Date
  ) {
    searchSaunas(
      lat: $lat
      lng: $lng
      minCapacity: $minCapacity
      maxCapacity: $maxCapacity
      maxDistanceMiles: $maxDistanceMiles
      minPriceCents: $minPriceCents
      maxPriceCents: $maxPriceCents
      saunaTypes: $saunaTypes
      amenityIds: $amenityIds
      filterDate: $filterDate
    ) {
      nodes {
        id name city state country capacity pricePerHourCents
        latitude longitude saunaType
        saunaImagesBySaunaId(first: 1, orderBy: DISPLAY_ORDER_ASC) {
          nodes { url }
        }
        reviewsBySaunaId(condition: { reviewerRole: "guest" }) {
          totalCount
          nodes { overall cleanliness communication accuracy valueScore }
        }
      }
    }
  }
`;

function parseIntOrNull(s: string | null): number | null {
  if (!s) return null;
  const n = parseInt(s, 10);
  return isNaN(n) ? null : n;
}

function parseFloatOrNull(s: string | null): number | null {
  if (!s) return null;
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

export default function Home() {
  usePageTitle("Lets Go Sauna — Book Private Saunas by the Hour");
  const [params] = useSearchParams();
  const isMobile = useIsMobile();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [showMap, setShowMap] = useState(!isMobile);

  const city = params.get("city");
  const lat = parseFloatOrNull(params.get("lat"));
  const lng = parseFloatOrNull(params.get("lng"));
  const radius = parseFloatOrNull(params.get("radius"));
  const minCap = parseIntOrNull(params.get("minCap"));
  const maxCap = parseIntOrNull(params.get("maxCap"));
  const minPrice = parseIntOrNull(params.get("minPrice"));
  const maxPrice = parseIntOrNull(params.get("maxPrice"));
  const typesRaw = params.get("types");
  const amenitiesRaw = params.get("amenities");
  const date = params.get("date");

  const saunaTypes = typesRaw ? typesRaw.split(",").filter(Boolean) : null;
  const amenityIds = amenitiesRaw
    ? amenitiesRaw.split(",").map(Number).filter(n => !isNaN(n) && n > 0)
    : null;

  const hasSearch = !!(city || date || minCap || maxCap || minPrice || maxPrice || saunaTypes?.length || amenityIds?.length);

  const variables = {
    lat,
    lng,
    minCapacity: minCap,
    maxCapacity: maxCap,
    maxDistanceMiles: lat && lng ? (radius ?? 100) : null,
    minPriceCents: minPrice,
    maxPriceCents: maxPrice,
    saunaTypes: saunaTypes?.length ? saunaTypes : null,
    amenityIds: amenityIds?.length ? amenityIds : null,
    filterDate: date || null,
  };

  const { data, loading, error } = useQuery(SEARCH_SAUNAS, {
    variables,
    fetchPolicy: "network-only",
  });

  const saunas: any[] = data?.searchSaunas?.nodes ?? [];

  if (!hasSearch) {
    return (
      <div>
        <div style={heroStyle}>
          <div style={heroInnerStyle}>
            <h1 style={heroHeadingStyle}>Find your perfect sauna.</h1>
            <p style={heroSubStyle}>Browse private saunas for rent by the hour — traditional Finnish, barrel, infrared, and more.</p>
            <div style={{ maxWidth: 600, margin: "0 auto" }}>
              <SearchBar />
            </div>
          </div>
        </div>

        <div style={{ maxWidth: 1280, margin: "40px auto", padding: "0 32px" }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 20 }}>Recently listed</h2>

          {loading && <p style={{ color: "#9ca3af" }}>Loading…</p>}
          {error && <p style={{ color: "#ef4444" }}>Error: {error.message}</p>}

          {!loading && !error && saunas.length === 0 && (
            <p style={{ color: "#9ca3af" }}>No saunas listed yet.</p>
          )}

          <div style={shelfGrid}>
            {saunas.map(s => (
              <SaunaCard key={s.id} sauna={s} variant="vertical" date={date} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 57px)" }}>
      {/* Sticky top bar */}
      <div style={stickyBarStyle}>
        <SearchBar compact onToggleFilters={() => setFiltersOpen(o => !o)} filtersOpen={filtersOpen} />
        {filtersOpen && <div style={{ marginTop: 8 }}><FilterBar /></div>}
      </div>

      {/* Results area */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>
        {/* Left: card list */}
        {(!isMobile || !showMap) && (
          <div style={isMobile ? { ...listColStyle, width: "100%" } : listColStyle}>
            <div style={{ padding: "12px 16px 6px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, color: "#6b7280" }}>
                {loading ? "Searching…" : `${saunas.length} sauna${saunas.length !== 1 ? "s" : ""} found`}
                {city ? ` near ${city}` : ""}
              </span>
              <button onClick={() => setShowMap(m => !m)} style={mapToggleBtn}>
                {showMap ? "Hide map" : "Show map"}
              </button>
            </div>

            {error && <p style={{ color: "#ef4444", padding: "0 16px" }}>Error: {error.message}</p>}
            {!loading && !error && saunas.length === 0 && (
              <p style={{ color: "#9ca3af", padding: "16px" }}>No saunas match your filters.</p>
            )}

            <div style={cardListStyle}>
              {saunas.map(s => (
                <SaunaCard
                  key={s.id}
                  sauna={s}
                  variant="horizontal"
                  highlighted={s.id === hoveredId}
                  onMouseEnter={() => setHoveredId(s.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  date={date}
                />
              ))}
            </div>
          </div>
        )}

        {/* Right: map */}
        {showMap && (
          <div style={isMobile ? { ...mapColStyle, width: "100%" } : mapColStyle}>
            <ResultsMap saunas={saunas} hoveredId={hoveredId} />
          </div>
        )}
      </div>

      {isMobile && showMap && (
        <button onClick={() => setShowMap(false)} style={backToListBtn}>
          ☰ Show list
        </button>
      )}
    </div>
  );
}

const heroStyle: React.CSSProperties = {
  background: "linear-gradient(135deg, #1e3a5f 0%, #2d6a4f 100%)",
  padding: "72px 32px",
  textAlign: "center",
};
const heroInnerStyle: React.CSSProperties = { maxWidth: 720, margin: "0 auto" };
const heroHeadingStyle: React.CSSProperties = { fontSize: 42, fontWeight: 700, color: "#fff", margin: "0 0 12px" };
const heroSubStyle: React.CSSProperties = { fontSize: 17, color: "rgba(255,255,255,0.85)", marginBottom: 28 };

const shelfGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
  gap: 24,
};

const stickyBarStyle: React.CSSProperties = {
  padding: "10px 16px",
  background: "#fff",
  borderBottom: "1px solid #e5e7eb",
  zIndex: 10,
};

const listColStyle: React.CSSProperties = {
  width: 380,
  flexShrink: 0,
  overflowY: "auto",
  borderRight: "1px solid #e5e7eb",
  display: "flex",
  flexDirection: "column",
};

const mapColStyle: React.CSSProperties = {
  flex: 1,
  position: "relative",
  padding: 12,
};

const cardListStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
  padding: "8px 12px 24px",
};

const mapToggleBtn: React.CSSProperties = {
  fontSize: 12, padding: "4px 10px", border: "1px solid #d1d5db",
  borderRadius: 6, background: "#fff", cursor: "pointer", color: "#374151",
};

const backToListBtn: React.CSSProperties = {
  position: "fixed",
  left: "50%",
  transform: "translateX(-50%)",
  bottom: "calc(20px + env(safe-area-inset-bottom))",
  zIndex: 1200,
  padding: "12px 24px",
  borderRadius: 999,
  border: "none",
  background: "#1f2937",
  color: "#fff",
  fontSize: 14,
  fontWeight: 600,
  cursor: "pointer",
  boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
};
